import { useQuery } from '@tanstack/react-query'
import { Loader2, Maximize2, Minimize2 } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useMemo, useRef, useState, useEffect } from 'react'

import { Button } from '@/components/elements/button'
import {
  getArticulateLaunchToken,
  getStudentCourseContentRecord,
} from '@/services/student_courses.service'
import { UserStoreContext } from '@/stores/UserStore'
import { cn } from '@/utils/helpers'

import Block from './Block'

const COMPLETED_STATUSES = ['Completed', 'Quiz Pending']

const BlockArticulate = observer(({ block, nextBlock, isLastBlock, upNextTitle }) => {
  const { activeEntity } = useContext(UserStoreContext)

  const initiallyCompleted = COMPLETED_STATUSES.includes(block.status)
  // The backend only emits articulateCourseUrl while a live extraction exists,
  // and keeps serving the previous publish while a replacement is reprocessing
  // (RSCX-363) — so gate on the URL rather than a strict Success status, which
  // would flash the error state during that window. A failed publish clears
  // the URL, so real errors still surface.
  const articulateIsLoaded = Boolean(block.content.articulateCourseUrl)

  // Poll for completion status every 5s until the backend confirms the xAPI
  // statement has been processed. This eliminates the race condition where
  // a user clicks "Complete" before the backend has finished handling the
  // Articulate course's xAPI callback.
  const { data: contentRecord } = useQuery({
    queryKey: ['accounts', 'students', 'course-content-record', activeEntity, block.id],
    queryFn: async () => getStudentCourseContentRecord(activeEntity, block.id),
    enabled: !initiallyCompleted && !!activeEntity,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false
      const status = query.state.data?.status
      if (COMPLETED_STATUSES.includes(status)) return false
      return 5000
    },
  })

  const isServerCompleted =
    initiallyCompleted || COMPLETED_STATUSES.includes(contentRecord?.status)

  const [isExpanded, setIsExpanded] = useState(false)

  // Mint a scoped launch token for the iframe's xAPI calls. Fetched once and kept
  // stable (the token outlives a course sitting), so unlike the learner's 30-min
  // access token it never needs refreshing — which is what used to reload the
  // player mid-course. See getArticulateLaunchToken.
  const {
    data: launchData,
    isLoading: isLaunchLoading,
    isError: isLaunchError,
  } = useQuery({
    queryKey: ['accounts', 'students', 'articulate-launch', activeEntity, block.id],
    queryFn: () => getArticulateLaunchToken(activeEntity, block.id),
    enabled: articulateIsLoaded && !!activeEntity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Handle expanded mode - prevent body scroll and listen for Escape
  useEffect(() => {
    if (!isExpanded) return undefined

    document.body.style.overflow = 'hidden'
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsExpanded(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isExpanded])

  // Freeze the embed URL once we have the launch token. The Articulate runtime
  // keeps using the token baked into this URL for the whole session; capturing it
  // in a ref means a re-render (or a token-query refetch) can't change the iframe
  // src and reload the player mid-course.
  const articulateCourseUrlRef = useRef(null)
  if (articulateCourseUrlRef.current === null && launchData?.token && activeEntity) {
    const base = import.meta.env.VITE_API_BASE_URL.replace('/api/v1/', '')

    const params = new URLSearchParams()
    params.append('auth', `Bearer ${launchData.token}`)
    params.append(
      'endpoint',
      `${import.meta.env.VITE_API_BASE_URL}entities/${activeEntity}/course-content-records/${block.id}/xapi/`,
    )
    // Minimal actor info needed so course doesn't crash
    params.append('actor', JSON.stringify({}))

    articulateCourseUrlRef.current = `${base}${block.content.articulateCourseUrl}?${params.toString()}`
  }
  const articulateCourseUrl = articulateCourseUrlRef.current

  // The player (and its in-frame Exit control) only renders once the launch token
  // has produced a URL with no error. Gate the Fullscreen button on the same
  // condition so it can't lock body scroll while a loader/error is showing with no
  // visible way out.
  const isPlayerReady =
    articulateIsLoaded && !isLaunchError && !isLaunchLoading && !!articulateCourseUrl

  const handleComplete = () => nextBlock({ completionTriggeredOnServer: true })

  const proceedButtonText = useMemo(() => {
    if (isLastBlock) return 'Finish module'
    const status = contentRecord?.status || block.status
    if (status === 'Quiz Pending' || block.content.quiz) return 'Take Quiz'
    return 'Next'
  }, [isLastBlock, contentRecord?.status, block.status, block.content.quiz])

  return (
    <Block
      canProceed={isServerCompleted}
      nextBlock={handleComplete}
      isLastBlock={isLastBlock}
      title={block.content.name ?? 'Articulate'}
      block={block}
      upNextTitle={upNextTitle}
      customButton={
        <div className="flex flex-wrap items-center justify-center gap-2">
          {isPlayerReady && (
            <Button
              variant="outline"
              onClick={() => setIsExpanded(true)}
              className="flex items-center gap-2"
            >
              <Maximize2 className="h-4 w-4" />
              <span className="hidden sm:inline">Fullscreen</span>
              <span className="sm:hidden">Expand</span>
            </Button>
          )}
          <Button onClick={handleComplete} disabled={!isServerCompleted}>
            {proceedButtonText}
          </Button>
        </div>
      }
    >
      {(() => {
        if (!articulateIsLoaded) {
          return (
            <div className="text-destructive">
              There was an error loading this Articulate course. Please contact support.
            </div>
          )
        }
        if (isLaunchError) {
          return (
            <div className="text-destructive">
              We couldn&apos;t start this Articulate course. Please refresh the page and try again.
            </div>
          )
        }
        if (isLaunchLoading || !articulateCourseUrl) {
          return (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          )
        }
        return (
          <div
            className={cn(
              'relative h-full w-full bg-black',
              isExpanded && 'fixed inset-0 z-[100]',
            )}
          >
            <iframe
              title="Articulate Course"
              src={articulateCourseUrl}
              className="h-full w-full"
              allowFullScreen
            />
            {/* Expanded controls overlay */}
            {isExpanded && (
              <div className="absolute top-4 right-4 z-10 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsExpanded(false)}
                  className="flex items-center gap-2 shadow-lg"
                >
                  <Minimize2 className="h-4 w-4" />
                  Exit
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsExpanded(false)
                    handleComplete()
                  }}
                  disabled={!isServerCompleted}
                  className="shadow-lg"
                >
                  {proceedButtonText}
                </Button>
              </div>
            )}
          </div>
        )
      })()}
    </Block>
  )
})
export default BlockArticulate

BlockArticulate.propTypes = {
  block: PropTypes.object.isRequired,
  nextBlock: PropTypes.func.isRequired,
  isLastBlock: PropTypes.bool,
  upNextTitle: PropTypes.string,
}
